// Admin panel — Provider & Gateway Balance Monitoring + Gateway Switching.
// Three sections on one page:
//   1. Provider Balances — live balance for every VTU provider + payment
//      gateway, with low-balance flags and per-provider refresh.
//   2. Payment Gateway Routing — switch which gateway each service uses
//   3. StroWallet Settlement — sweep your StroWallet balance to your own
//      bank account (resolve → confirm → withdraw → poll status)
import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import {
  getProviderBalances, refreshProviderBalance, setProviderThreshold,
  getGatewayStatus, switchGateway, testGateway, enableGateway, disableGateway,
  apiClient,
} from "../../../api/apiServices";

// ─── Static metadata ──────────────────────────────────────────
const PROVIDER_META = {
  vtpass:         { label: "VTPass",         color: "#059669", icon: "ri-server-line" },
  paygold:        { label: "Paygold",        color: "#0284C7", icon: "ri-bank-card-line" },
  vtung:          { label: "VTU.ng",         color: "#7C3AED", icon: "ri-shield-line" },
  airtimenigeria: { label: "AirtimeNigeria", color: "#DC2626", icon: "ri-phone-fill" },
  paystack:       { label: "Paystack",       color: "#00C896", icon: "ri-bank-line" },
  flutterwave:    { label: "Flutterwave",    color: "#F59E0B", icon: "ri-flashlight-fill" },
  ivorypay:       { label: "IvoryPay",       color: "#00C896", icon: "ri-bank-line" },
  korapay:        { label: "Korapay",        color: "#FF5A5F", icon: "ri-secure-payment-line" },
  strowallet:     { label: "Strowallet",     color: "#F59E0B", icon: "ri-bank-line" },

};

const SERVICE_META = {
  payment_link:    { label: "Payment Links",         icon: "ri-link" },
  payroll:         { label: "Payroll",                icon: "ri-team-line" },
  deposit:         { label: "Deposits (new modules)", icon: "ri-arrow-down-circle-line" },
  withdrawal:      { label: "Withdrawals",            icon: "ri-arrow-up-circle-line" },
  virtual_account: { label: "Virtual Accounts",       icon: "ri-bank-card-2-line" },
  // virtual_card: { label: "Virtual Card",       icon: "ri-bank-card-2-line" },
};

function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 10) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

function fmtNaira(n) {
  if (n === null || n === undefined) return "—";
  return `₦${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

// ── Single balance card ────────────────────────────────────────
function BalanceCard({ data, onRefresh, onEditThreshold, refreshing }) {
  const meta = PROVIDER_META[data.provider] || { label: data.provider, color: "#6B7280", icon: "ri-question-line" };
  const isLow = data.low;
  const failed = !data.ok;

  return (
    <div className="rounded-2xl border p-4 transition-all"
      style={{
        background:  failed ? "rgba(239,68,68,0.05)" : isLow ? "rgba(245,158,11,0.06)" : "#161616",
        borderColor: failed ? "rgba(239,68,68,0.25)" : isLow ? "rgba(245,158,11,0.25)" : "rgba(255,255,255,0.06)",
      }}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: meta.color + "18" }}>
            <i className={`${meta.icon} text-base`} style={{ color: meta.color }} />
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-semibold truncate">{meta.label}</p>
            <p className="text-white/30 text-[10px]">Checked {timeAgo(data.checkedAt)}</p>
          </div>
        </div>
        <button
          onClick={() => onRefresh(data.provider)}
          disabled={refreshing}
          className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/40 hover:text-white transition-colors flex-shrink-0"
          title="Refresh balance"
        >
          <i className={`ri-refresh-line text-xs ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      {failed ? (
        <div className="flex items-center gap-2 text-red-400 text-xs">
          <i className="ri-error-warning-line flex-shrink-0" />
          <span className="truncate">{data.error || "Balance check failed"}</span>
        </div>
      ) : (
        <>
          <p className="text-2xl font-bold text-white mb-1 break-all" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {fmtNaira(data.balance)}
          </p>

          {/* Sub-wallets — e.g. AirtimeNigeria's SMS/MTN-data/Airtel-EDS/Glo-CG float pools.
              Shown separately since they're not part of the main NGN balance above —
              and some (like mtn_data_wallet) aren't even denominated in currency. */}
          {data.subWallets && Object.values(data.subWallets).some(Boolean) && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {Object.entries(data.subWallets).filter(([, v]) => v).map(([key, w]) => (
                <span key={key} className="text-[9px] text-white/35 bg-white/5 px-1.5 py-0.5 rounded-md">
                  {key.replace(/_/g, " ")}: {w.currency === "NGN" ? fmtNaira(w.balance) : `${w.balance.toLocaleString()} ${w.currency}`}
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 flex-wrap">
            <button
              onClick={() => onEditThreshold(data.provider, data.threshold)}
              className="text-[10px] text-white/30 hover:text-white/60 transition-colors"
            >
              Alert below {fmtNaira(data.threshold)}
            </button>
            {isLow && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 flex-shrink-0">
                <i className="ri-alert-fill text-[9px]" />
                Low balance
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Threshold edit modal (simple inline) ───────────────────────
function ThresholdModal({ provider, currentValue, onClose, onSave }) {
  const [value, setValue] = useState(currentValue || 0);
  const [saving, setSaving] = useState(false);
  const meta = PROVIDER_META[provider] || { label: provider };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(provider, Number(value));
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4" onClick={onClose}>
      <div className="bg-[#161616] rounded-2xl border border-white/8 p-5 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <h3 className="text-white font-bold text-sm mb-1">Low-balance alert — {meta.label}</h3>
        <p className="text-white/40 text-xs mb-4">You'll see a "Low balance" flag when this provider drops below the amount set here.</p>
        <input
          type="number"
          value={value}
          onChange={e => setValue(e.target.value)}
          className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-[#00C896]/50 mb-4"
          placeholder="e.g. 20000"
        />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-white/50 bg-white/5 hover:bg-white/10 transition-colors">
            Cancel
          </button>
          <button onClick={save} disabled={saving} className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-black bg-[#00C896] hover:opacity-90 transition-opacity disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Gateway routing row ─────────────────────────────────────────
// FIXED: was a single `flex items-center justify-between` row — with 5
// gateway buttons (paystack/flutterwave/ivorypay/korapay/strowallet) plus
// a service label, this overflowed/squished badly below ~640px. Now
// stacks the label above the button group on mobile and lets the button
// group wrap onto multiple lines instead of overflowing horizontally.
function GatewayServiceRow({ serviceKey, activeGateway, gateways, onSwitch, onEnableClick, switching }) {
  const meta = SERVICE_META[serviceKey] || { label: serviceKey, icon: "ri-settings-3-line" };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 py-3 border-b border-white/5 last:border-0">
      <div className="flex items-center gap-3 flex-shrink-0">
        <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
          <i className={`${meta.icon} text-sm text-white/50`} />
        </div>
        <p className="text-white text-sm font-medium">{meta.label}</p>
      </div>
      <div className="flex flex-wrap gap-2 sm:justify-end">
        {gateways.map(g => {
          const isActive = g.key === activeGateway;
          const gMeta = PROVIDER_META[g.key] || { label: g.key, color: "#6B7280" };

          if (!g.enabled) {
            return (
              <button
                key={g.key}
                onClick={() => onEnableClick(g.key)}
                title={`${gMeta.label} isn't enabled yet — click to test credentials and enable`}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 border border-dashed whitespace-nowrap"
                style={{ color: "rgba(255,255,255,0.35)", borderColor: "rgba(255,255,255,0.15)" }}
              >
                <i className="ri-lock-line text-[10px]" />
                {gMeta.label}
              </button>
            );
          }

          return (
            <button
              key={g.key}
              disabled={isActive || switching}
              onClick={() => onSwitch(serviceKey, g.key)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap"
              style={{
                background: isActive ? gMeta.color + "20" : "rgba(255,255,255,0.04)",
                color:      isActive ? gMeta.color : "rgba(255,255,255,0.5)",
                border:     `1px solid ${isActive ? gMeta.color + "40" : "transparent"}`,
                cursor:     isActive ? "default" : "pointer",
              }}
            >
              {isActive && <i className="ri-checkbox-circle-fill mr-1" />}
              {gMeta.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Enable-gateway modal: shows test result before committing ──
function EnableGatewayModal({ gateway, onClose, onEnabled }) {
  const meta = PROVIDER_META[gateway] || { label: gateway };
  const [testing, setTesting]   = useState(true);
  const [testResult, setTestResult] = useState(null);
  const [enabling, setEnabling] = useState(false);

  useEffect(() => {
    testGateway(gateway)
      .then(res => setTestResult(res))
      .catch(err => setTestResult({ status: false, message: err?.response?.data?.message || "Test failed" }))
      .finally(() => setTesting(false));
  }, [gateway]);

  const handleEnable = async () => {
    setEnabling(true);
    try {
      const res = await enableGateway(gateway);
      if (res.status) {
        toast.success(res.message);
        onEnabled();
        onClose();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to enable gateway");
    } finally {
      setEnabling(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4" onClick={onClose}>
      <div className="bg-[#161616] rounded-2xl border border-white/8 p-5 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <h3 className="text-white font-bold text-sm mb-1">Enable {meta.label}</h3>
        <p className="text-white/40 text-xs mb-4">
          We'll test the API credentials configured in your environment before enabling this gateway for use.
        </p>

        {testing ? (
          <div className="flex items-center gap-2 text-white/50 text-xs py-3">
            <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
            Testing credentials…
          </div>
        ) : testResult?.status ? (
          <div className="flex items-start gap-2 text-emerald-400 text-xs bg-emerald-500/8 border border-emerald-500/20 rounded-xl px-3 py-2.5 mb-4">
            <i className="ri-checkbox-circle-fill mt-0.5" />
            <span>Credentials valid. Balance: {testResult.balance?.toLocaleString()} {testResult.currency}</span>
          </div>
        ) : (
          <div className="flex items-start gap-2 text-red-400 text-xs bg-red-500/8 border border-red-500/20 rounded-xl px-3 py-2.5 mb-4">
            <i className="ri-error-warning-fill mt-0.5" />
            <span>{testResult?.message || "Credential test failed"}</span>
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-white/50 bg-white/5 hover:bg-white/10 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleEnable}
            disabled={testing || !testResult?.status || enabling}
            className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-black bg-[#00C896] hover:opacity-90 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {enabling ? "Enabling…" : "Enable gateway"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// STROWALLET SETTLEMENT PANEL
// Sweep StroWallet's held balance (from virtual account deposits) to
// your own external bank account. Hits adminStrowalletRoutes.js
// directly via apiClient — no dedicated apiServices.js helpers exist
// for these yet, so this calls the endpoints inline, same low-level
// pattern FiatDeposit.jsx already uses for calls that don't have a
// named service function.
//
// Flow: pick bank + enter account number → Resolve (name enquiry) →
// confirm the resolved name is right → enter amount → Withdraw →
// poll status. The resolve step's result is short-lived per
// strowallet.js's own warning, so this panel does NOT let you sit on a
// resolved account for long — resolving again is cheap, re-using a
// stale reference risks a failed transfer.
// ─────────────────────────────────────────────────────────────
function StrowalletSettlementPanel() {
  const [balance, setBalance]       = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [banks, setBanks]           = useState([]);
  const [banksLoading, setBanksLoading] = useState(true);

  const [bankCode, setBankCode]         = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [resolving, setResolving]       = useState(false);
  const [resolved, setResolved]         = useState(null); // { accountName, nameEnquiryReference }
  const [resolvedAt, setResolvedAt]     = useState(null);

  const [amount, setAmount]         = useState("");
  const [narration, setNarration]   = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [pendingTransfer, setPendingTransfer] = useState(null); // { transferCode, status }
  const [polling, setPolling]       = useState(false);
  const pollTimerRef = useRef(null);

  const loadBalance = useCallback(async () => {
    setBalanceLoading(true);
    try {
      const { data } = await apiClient.get("/mtrade/admin/strowallet/balance");
      if (data.status) setBalance(data.data);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load StroWallet balance");
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  const loadBanks = useCallback(async () => {
    setBanksLoading(true);
    try {
      const { data } = await apiClient.get("/mtrade/admin/strowallet/banks");
      if (data.status) {
        // FIXED — confirmed real response shape is
        // { status: true, data: { bank_list: [{ bankCode, bankName }] } }.
        // The old fallback chain (data.data?.banks / data.data?.data)
        // never matched "bank_list", so this always resolved to [].
        const list = Array.isArray(data.data)
          ? data.data
          : (data.data?.bank_list || data.data?.banks || data.data?.data || []);
        setBanks(list);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load bank list");
    } finally {
      setBanksLoading(false);
    }
  }, []);

  useEffect(() => { loadBalance(); loadBanks(); }, [loadBalance, loadBanks]);
  useEffect(() => () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); }, []);

  // Resolving a new account (or changing bank/account number) always
  // invalidates whatever was resolved before — never let a stale
  // resolution be used against a different bank/account pair.
  const invalidateResolution = () => { setResolved(null); setResolvedAt(null); };

  const handleResolve = async () => {
    if (!bankCode || !accountNumber) {
      toast.error("Choose a bank and enter an account number first");
      return;
    }
    setResolving(true);
    invalidateResolution();
    try {
      const { data } = await apiClient.post("/mtrade/admin/strowallet/resolve-account", { bankCode, accountNumber });
      if (data.status) {
        setResolved(data.data);
        setResolvedAt(Date.now());
        toast.success(`Resolved: ${data.data.accountName}`);
      } else {
        toast.error(data.message || "Could not resolve account name");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to resolve account");
    } finally {
      setResolving(false);
    }
  };

  const handleWithdraw = async () => {
    if (!resolved?.nameEnquiryReference) {
      toast.error("Resolve the account first");
      return;
    }
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (!window.confirm(`Send ${fmtNaira(amt)} to ${resolved.accountName} (${accountNumber})? This cannot be undone.`)) {
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await apiClient.post("/mtrade/admin/strowallet/withdraw", {
        bankCode,
        accountNumber,
        nameEnquiryReference: resolved.nameEnquiryReference,
        amount: amt,
        narration,
      });

      if (data.status) {
        toast.success("Settlement transfer initiated");
        setPendingTransfer(data.data);
        startPolling(data.data.transferCode);
        // Reset the form for the next transfer, but keep the pending
        // status panel visible until it resolves.
        setAmount(""); setNarration(""); invalidateResolution(); setAccountNumber(""); setBankCode("");
      } else {
        toast.error(data.message || "Withdrawal failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Withdrawal request failed");
    } finally {
      setSubmitting(false);
    }
  };

  const checkStatus = useCallback(async (transferCode) => {
    try {
      const { data } = await apiClient.get(`/mtrade/admin/strowallet/withdraw/${transferCode}/status`);
      if (data.status) {
        setPendingTransfer(prev => prev ? { ...prev, status: data.data.status } : prev);
        if (data.data.success || data.data.failed) {
          stopPolling();
          toast[data.data.success ? "success" : "error"](
            data.data.success ? "Settlement transfer completed" : "Settlement transfer failed"
          );
          loadBalance(); // refresh balance now that it's moved (or didn't)
        }
      }
    } catch {
      // Silent on poll failures — don't spam toasts every 5s if StroWallet's API hiccups.
    }
  }, [loadBalance]);

  const startPolling = (transferCode) => {
    setPolling(true);
    pollTimerRef.current = setInterval(() => checkStatus(transferCode), 5000);
  };
  const stopPolling = () => {
    setPolling(false);
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
  };

  // A resolution older than 90s is flagged as likely-stale — the actual
  // expiry window isn't documented, this is a conservative UI nudge, not
  // an enforced cutoff (the backend will reject it if it's genuinely
  // expired regardless of what this shows).
  const resolutionAgeSeconds = resolvedAt ? Math.floor((Date.now() - resolvedAt) / 1000) : null;
  const resolutionLikelyStale = resolutionAgeSeconds !== null && resolutionAgeSeconds > 90;

  return (
    <div className="rounded-2xl bg-[#161616] border border-white/6 p-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <div>
          <h2 className="text-white text-base font-bold">StroWallet Settlement</h2>
          <p className="text-white/30 text-xs mt-0.5">Move your StroWallet balance to your own bank account</p>
        </div>
        <div className="flex items-center gap-2">
          {balanceLoading ? (
            <div className="w-24 h-6 bg-white/5 rounded-lg animate-pulse" />
          ) : (
            <span className="text-white text-sm font-bold px-3 py-1.5 rounded-lg bg-white/5">
              {fmtNaira(balance?.balance)} available
            </span>
          )}
          <button onClick={loadBalance} className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/40 hover:text-white transition-colors flex-shrink-0">
            <i className="ri-refresh-line text-xs" />
          </button>
        </div>
      </div>

      {/* Pending transfer status — shown until it resolves */}
      {pendingTransfer && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 px-4 py-3 rounded-xl bg-amber-500/8 border border-amber-500/20">
          <div className="flex items-center gap-2 min-w-0">
            {polling && <div className="w-3.5 h-3.5 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin flex-shrink-0" />}
            <p className="text-amber-300 text-xs truncate">
              Transfer <span className="font-mono">{pendingTransfer.transferCode}</span> — {pendingTransfer.status || "processing"}
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={() => checkStatus(pendingTransfer.transferCode)} className="text-[10px] font-semibold text-amber-300 hover:text-amber-200 px-2 py-1 rounded-md bg-amber-500/10">
              Check now
            </button>
            <button onClick={() => { stopPolling(); setPendingTransfer(null); }} className="text-[10px] font-semibold text-white/30 hover:text-white/60 px-2 py-1 rounded-md bg-white/5">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Form: responsive 2-col on larger screens, stacked on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Step 1: resolve */}
        <div className="space-y-3">
          <p className="text-white/50 text-[10px] font-bold uppercase tracking-wider">1. Resolve account</p>

          <select
            value={bankCode}
            onChange={e => { setBankCode(e.target.value); invalidateResolution(); }}
            disabled={banksLoading}
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-[#00C896]/50 disabled:opacity-50"
          >
            <option value="">{banksLoading ? "Loading banks…" : "Select bank"}</option>
            {banks.map(b => {
              // FIXED — real bank objects use bankCode/bankName
              // (camelCase), not code/bank_code/name/bank_name/label.
              const code = b.bankCode || b.code || b.bank_code || b.id;
              const name = b.bankName || b.name || b.bank_name || b.label;
              return (
                <option key={code} value={code}>
                  {name}
                </option>
              );
            })}
          </select>

          <input
            type="text"
            value={accountNumber}
            onChange={e => { setAccountNumber(e.target.value); invalidateResolution(); }}
            placeholder="Account number"
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-[#00C896]/50"
          />

          <button
            onClick={handleResolve}
            disabled={resolving || !bankCode || !accountNumber}
            className="w-full py-2.5 rounded-xl text-xs font-semibold text-black bg-white/80 hover:bg-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {resolving ? "Resolving…" : "Resolve account name"}
          </button>

          {resolved && (
            <div className={`flex items-start gap-2 text-xs rounded-xl px-3 py-2.5 border ${
              resolutionLikelyStale ? "bg-amber-500/8 border-amber-500/20 text-amber-300" : "bg-emerald-500/8 border-emerald-500/20 text-emerald-400"
            }`}>
              <i className={`${resolutionLikelyStale ? "ri-time-line" : "ri-checkbox-circle-fill"} mt-0.5 flex-shrink-0`} />
              <span>
                {resolved.accountName}
                {resolutionLikelyStale && " — this resolution may have expired, consider resolving again before withdrawing"}
              </span>
            </div>
          )}
        </div>

        {/* Step 2: withdraw */}
        <div className="space-y-3">
          <p className="text-white/50 text-[10px] font-bold uppercase tracking-wider">2. Confirm & withdraw</p>

          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm">₦</span>
            <input
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              disabled={!resolved}
              placeholder="Amount"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl pl-7 pr-3 py-2.5 text-white text-sm outline-none focus:border-[#00C896]/50 disabled:opacity-40"
            />
          </div>

          <input
            type="text"
            value={narration}
            onChange={e => setNarration(e.target.value)}
            disabled={!resolved}
            placeholder="Narration (optional)"
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-[#00C896]/50 disabled:opacity-40"
          />

          <button
            onClick={handleWithdraw}
            disabled={!resolved || submitting || resolutionLikelyStale}
            className="w-full py-2.5 rounded-xl text-xs font-semibold text-black bg-[#00C896] hover:opacity-90 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {submitting ? "Sending…" : "Withdraw to bank"}
          </button>

          <p className="text-white/25 text-[10px] leading-relaxed">
            StroWallet gives no idempotency key for this transfer — if a request times out, check its status
            before ever retrying rather than submitting again.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
export default function ProviderBalancesView() {
  const [loading, setLoading]       = useState(true);
  const [balanceData, setBalanceData] = useState(null);
  const [refreshingKey, setRefreshingKey] = useState(null);
  const [thresholdModal, setThresholdModal] = useState(null);

  const [gatewayData, setGatewayData] = useState(null);
  const [switchingService, setSwitchingService] = useState(null);
  const [enableModalGateway, setEnableModalGateway] = useState(null);

  const loadBalances = useCallback(async () => {
    try {
      const res = await getProviderBalances();
      if (res.status) setBalanceData(res);
    } catch {
      toast.error("Failed to load provider balances");
    }
  }, []);

  const loadGateways = useCallback(async () => {
    try {
      const res = await getGatewayStatus();
      if (res.status) setGatewayData(res);
    } catch {
      toast.error("Failed to load gateway status");
    }
  }, []);

  useEffect(() => {
    Promise.all([loadBalances(), loadGateways()]).finally(() => setLoading(false));
    // Auto-refresh balances every 2 minutes
    const interval = setInterval(loadBalances, 120_000);
    return () => clearInterval(interval);
  }, [loadBalances, loadGateways]);

  const handleRefresh = async (provider) => {
    setRefreshingKey(provider);
    try {
      const res = await refreshProviderBalance(provider);
      if (res.status) {
        // Patch just this one provider into state without a full reload
        setBalanceData(prev => {
          if (!prev) return prev;
          const patch = (list) => list.map(p => p.provider === provider ? { ...p, ...res } : p);
          return { ...prev, vtuProviders: patch(prev.vtuProviders), paymentGateways: patch(prev.paymentGateways) };
        });
      }
    } catch {
      toast.error(`Failed to refresh ${provider}`);
    } finally {
      setRefreshingKey(null);
    }
  };

  const handleSaveThreshold = async (provider, amount) => {
    try {
      await setProviderThreshold(provider, amount);
      toast.success("Threshold updated");
      loadBalances();
    } catch {
      toast.error("Failed to update threshold");
    }
  };

  const handleSwitchGateway = async (service, gateway) => {
    setSwitchingService(service);
    try {
      const res = await switchGateway(service, gateway);
      if (res.status) {
        toast.success(res.message);
        loadGateways();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to switch gateway");
    } finally {
      setSwitchingService(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-white/10 border-t-[#00C896] rounded-full animate-spin" />
      </div>
    );
  }

  const allBalances = [...(balanceData?.vtuProviders || []), ...(balanceData?.paymentGateways || [])];

  return (
    <div className="space-y-6 px-3 sm:px-0">

      {/* ── Summary banner ── */}
      {balanceData?.summary?.anyLow && (
        <div className="flex items-start sm:items-center gap-3 px-4 py-3 rounded-2xl bg-amber-500/8 border border-amber-500/20">
          <i className="ri-alert-fill text-amber-400 text-lg flex-shrink-0" />
          <p className="text-amber-300 text-sm font-medium">One or more providers are below their low-balance threshold. Top up to avoid failed transactions.</p>
        </div>
      )}
      {balanceData?.summary?.anyFailed && (
        <div className="flex items-start sm:items-center gap-3 px-4 py-3 rounded-2xl bg-red-500/8 border border-red-500/20">
          <i className="ri-error-warning-fill text-red-400 text-lg flex-shrink-0" />
          <p className="text-red-300 text-sm font-medium">One or more balance checks failed — see details on the affected card below.</p>
        </div>
      )}

      {/* ── Provider balances ── */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <div>
            <h2 className="text-white text-base font-bold">Provider & Gateway Balances</h2>
            <p className="text-white/30 text-xs mt-0.5">Live float balances across all VTU providers and payment gateways</p>
          </div>
          <button
            onClick={loadBalances}
            className="flex items-center gap-1.5 text-xs font-semibold text-white/50 hover:text-white px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors self-start sm:self-auto"
          >
            <i className="ri-refresh-line" /> Refresh all
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {allBalances.map(data => (
            <BalanceCard
              key={data.provider}
              data={data}
              refreshing={refreshingKey === data.provider}
              onRefresh={handleRefresh}
              onEditThreshold={(provider, current) => setThresholdModal({ provider, current })}
            />
          ))}
        </div>
      </div>

      {/* ── Gateway routing ── */}
      <div className="rounded-2xl bg-[#161616] border border-white/6 p-4 sm:p-5">
        <div className="mb-1">
          <h2 className="text-white text-base font-bold">Payment Gateway Routing</h2>
          <p className="text-white/30 text-xs mt-0.5">
          Choose which gateway processes each service. Changes apply immediately to live traffic.
          </p>
        </div>

        <div className="mt-3">
          {(gatewayData?.services || []).map(serviceKey => (
            <GatewayServiceRow
              key={serviceKey}
              serviceKey={serviceKey}
              activeGateway={gatewayData?.activeByService?.[serviceKey]}
              gateways={gatewayData?.gateways || []}
              onSwitch={handleSwitchGateway}
              onEnableClick={(gateway) => setEnableModalGateway(gateway)}
              switching={switchingService === serviceKey}
            />
          ))}
        </div>

        {gatewayData?.gateways?.some(g => !g.enabled) && (
          <p className="text-white/25 text-[10px] mt-3 flex items-start gap-1.5">
            <i className="ri-information-line flex-shrink-0 mt-0.5" />
            Greyed-out gateways aren't enabled yet — click one to test its credentials and enable it.
          </p>
        )}
      </div>

      {/* ── StroWallet settlement ── */}
      <StrowalletSettlementPanel />

      {enableModalGateway && (
        <EnableGatewayModal
          gateway={enableModalGateway}
          onClose={() => setEnableModalGateway(null)}
          onEnabled={loadGateways}
        />
      )}

      {thresholdModal && (
        <ThresholdModal
          provider={thresholdModal.provider}
          currentValue={thresholdModal.current}
          onClose={() => setThresholdModal(null)}
          onSave={handleSaveThreshold}
        />
      )}
    </div>
  );
}